import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Trash2, ChevronDown, ChevronRight, User as UserIcon, Lock, Paperclip, X as XIcon, FileText, Image as ImageIcon, SmilePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const ROLE_TINT = {
  admin: "text-primary",
  innendienst: "text-primary",
  monteur: "text-emerald-600 dark:text-emerald-400",
  auftraggeber: "text-sky-600 dark:text-sky-400",
};

const REACTIONS = ["👍", "❤️", "✅", "❓", "🎉", "👀"];

export function ProjectNotes({ projectId, unread = 0, onRead, internal = false }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [reactionPickerFor, setReactionPickerFor] = useState(null);
  const [mentionState, setMentionState] = useState({ open: false, query: "", index: 0, caret: 0 });
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const isStaff = ["admin", "innendienst"].includes(user?.role);
  const qs = internal ? "?internal=true" : "";

  // Load mention candidates once (staff sees all, others see staff)
  useEffect(() => {
    api.get("/messages/users").then((r) => setMentionCandidates(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/notes${qs}`);
      setNotes(data || []);
      setLoaded(true);
      try {
        await api.post(`/projects/${projectId}/notes/read${qs}`);
        onRead?.(projectId, internal);
      } catch { /* silent */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nachrichten konnten nicht geladen werden");
    }
  };

  useEffect(() => {
    if (open && !loaded) load();
  }, [open, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
  }, [notes.length, open]);

  const onBodyChange = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart || val.length;
    setBody(val);
    // Detect @-mention in progress: last "@" up to caret
    const upto = val.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at >= 0) {
      const after = upto.slice(at + 1);
      // stop at whitespace / newline
      if (!/\s/.test(after) && after.length < 40) {
        setMentionState({ open: true, query: after, index: 0, caret: at });
        return;
      }
    }
    setMentionState((s) => ({ ...s, open: false }));
  };

  const filteredMentions = mentionState.open
    ? mentionCandidates.filter((u) =>
        (u.name || u.email || "").toLowerCase().includes(mentionState.query.toLowerCase())
      ).slice(0, 6)
    : [];

  const insertMention = (u) => {
    const name = (u.name || (u.email || "").split("@")[0] || "").trim();
    const before = body.slice(0, mentionState.caret);
    const rest = body.slice(mentionState.caret + 1 + mentionState.query.length);
    const next = `${before}@${name} ${rest}`;
    setBody(next);
    setMentionState({ open: false, query: "", index: 0, caret: 0 });
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const toggleReaction = async (noteId, emoji) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/notes/${noteId}/reactions`, { emoji });
      setNotes((ns) => ns.map((n) => (n.id === noteId ? { ...n, reactions: data.reactions } : n)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reaktion fehlgeschlagen");
    }
    setReactionPickerFor(null);
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text && pendingFiles.length === 0) return;
    setSending(true);
    try {
      const { data: note } = await api.post(`/projects/${projectId}/notes`, {
        body: text || (pendingFiles.length ? "📎 Anhang" : ""),
        internal,
      });
      // Upload attachments in parallel
      let uploaded = [];
      if (pendingFiles.length) {
        const results = await Promise.all(pendingFiles.map(async (f) => {
          const fd = new FormData();
          fd.append("file", f);
          try {
            const { data } = await api.post(
              `/projects/${projectId}/notes/${note.id}/attachments`,
              fd,
              { headers: { "Content-Type": "multipart/form-data" } },
            );
            return data;
          } catch (err) {
            toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen"}`);
            return null;
          }
        }));
        uploaded = results.filter(Boolean);
      }
      setNotes((n) => [...n, { ...note, attachments: uploaded }]);
      setBody("");
      setPendingFiles([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Senden fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  const onFilesChosen = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    // Max 3, max 15 MB each
    const filtered = list.slice(0, 3 - pendingFiles.length).filter((f) => {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: max. 15 MB`); return false; }
      return true;
    });
    setPendingFiles((s) => [...s, ...filtered]);
  };

  const remove = async (id) => {
    if (!confirm("Nachricht löschen?")) return;
    try {
      await api.delete(`/projects/${projectId}/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className={`rounded border mt-2 ${internal ? "border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10" : "border-border/70"}`} data-testid={`project-notes-${internal ? "internal-" : ""}${projectId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/40 text-left"
        data-testid={`project-notes-toggle-${internal ? "internal-" : ""}${projectId}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {internal ? <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" /> : <MessageSquare className="h-4 w-4 text-primary shrink-0" />}
          <span className="text-sm font-medium">
            {internal ? "Interne Notizen" : "Kunden-Nachrichten"}
          </span>
          {internal && <span className="text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold">Nur Admin/Innendienst</span>}
          <span className="text-[11px] text-muted-foreground">({loaded ? notes.length : "—"})</span>
          {unread > 0 && (
            <span className={`rounded-full text-[10px] font-bold px-1.5 min-w-[1.25rem] h-4 inline-flex items-center justify-center ml-1 ${internal ? "bg-amber-600 text-white" : "bg-destructive text-destructive-foreground"}`} data-testid={`project-notes-unread-${internal ? "internal-" : ""}${projectId}`}>
              {unread}
            </span>
          )}
        </div>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>

      {open && (
        <div className="border-t border-border/70 p-3 space-y-3 bg-secondary/10">
          <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
            {notes.length === 0 ? (
              <div className="text-[11px] text-muted-foreground text-center py-3">
                {internal
                  ? "Noch keine internen Notizen — nur Admin & Innendienst sehen diesen Bereich."
                  : "Noch keine Nachrichten — schreib die erste für alle mit Zugriff auf diesen Kunden."}
              </div>
            ) : notes.map((n) => {
              const own = n.user_id === user?.id;
              const tint = ROLE_TINT[n.user_role] || "text-muted-foreground";
              return (
                <div key={n.id} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`project-note-${n.id}`}>
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm border ${own ? "bg-primary/10 border-primary/30" : "bg-card border-border"}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${tint}`}>
                        <UserIcon className="h-3 w-3" />
                        {n.user_name || "—"}
                        <span className="text-muted-foreground normal-case font-normal tracking-normal ml-1">{fmtWhen(n.created_at)}</span>
                      </div>
                      {(isStaff || own) && (
                        <button
                          type="button"
                          onClick={() => remove(n.id)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`project-note-delete-${n.id}`}
                          title="Löschen"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    <div className="whitespace-pre-wrap break-words text-sm">
                      {(n.body || "").split(/(@[A-Za-zÄÖÜäöüß][\w.\-]*)/g).map((part, i) =>
                        part.startsWith("@") ? (
                          <span key={i} className="text-primary font-medium bg-primary/10 rounded px-1">{part}</span>
                        ) : (
                          <span key={i}>{part}</span>
                        )
                      )}
                    </div>
                    {(n.attachments?.length || 0) > 0 && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {n.attachments.map((a) => {
                          const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${a.path}`;
                          const isImg = (a.content_type || "").startsWith("image/");
                          return (
                            <a key={a.id} href={url} target="_blank" rel="noreferrer" className="block rounded border border-border overflow-hidden hover:border-primary/60" data-testid={`note-att-${a.id}`}>
                              {isImg ? (
                                <img src={url} alt={a.name} loading="lazy" className="w-full h-24 object-cover" />
                              ) : (
                                <div className="w-full h-24 flex flex-col items-center justify-center bg-secondary/40 text-muted-foreground gap-1">
                                  <FileText className="h-6 w-6" />
                                  <span className="text-[9px] uppercase tracking-wider">{(a.name.split(".").pop() || "Datei").toUpperCase()}</span>
                                </div>
                              )}
                              <div className="px-1.5 py-1 text-[10px] truncate" title={a.name}>{a.name}</div>
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {/* Reactions */}
                    <div className="mt-2 flex items-center gap-1 flex-wrap">
                      {Object.entries(n.reactions || {}).map(([emo, uids]) => {
                        const mine = uids?.includes(user?.id);
                        return (
                          <button
                            key={emo}
                            type="button"
                            onClick={() => toggleReaction(n.id, emo)}
                            className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] hover:bg-secondary/60 ${mine ? "border-primary bg-primary/10" : "border-border bg-background"}`}
                            data-testid={`reaction-${n.id}-${emo}`}
                          >
                            <span>{emo}</span>
                            <span className="text-[10px] text-muted-foreground">{uids.length}</span>
                          </button>
                        );
                      })}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setReactionPickerFor(reactionPickerFor === n.id ? null : n.id)}
                          className="text-muted-foreground hover:text-primary p-0.5"
                          data-testid={`reaction-open-${n.id}`}
                          title="Reagieren"
                        >
                          <SmilePlus className="h-3.5 w-3.5" />
                        </button>
                        {reactionPickerFor === n.id && (
                          <div className="absolute z-10 bottom-full mb-1 left-0 flex items-center gap-1 bg-popover border border-border rounded-full shadow-md px-1.5 py-1">
                            {REACTIONS.map((emo) => (
                              <button
                                key={emo}
                                type="button"
                                onClick={() => toggleReaction(n.id, emo)}
                                className="text-base hover:scale-125 transition-transform"
                                data-testid={`reaction-pick-${n.id}-${emo}`}
                              >
                                {emo}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="note-pending-files">
              {pendingFiles.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/30 px-2 py-1 text-[11px]">
                  {f.type.startsWith("image/") ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                  <span className="truncate max-w-[140px]" title={f.name}>{f.name}</span>
                  <button type="button" onClick={() => setPendingFiles((s) => s.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive">
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <form onSubmit={send} className="flex items-end gap-2 relative">
            {mentionState.open && filteredMentions.length > 0 && (
              <div className="absolute bottom-full left-0 mb-1 bg-popover border border-border rounded-md shadow-lg z-20 min-w-[220px] max-h-56 overflow-y-auto" data-testid="mention-popup">
                {filteredMentions.map((u, i) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => insertMention(u)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary/60 ${i === mentionState.index ? "bg-secondary/40" : ""}`}
                    data-testid={`mention-option-${u.id}`}
                  >
                    <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="truncate">{u.name || u.email}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{u.role} · {u.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={inputRef}
              value={body}
              onChange={onBodyChange}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
              placeholder={internal
                ? "Interne Notiz — @Name um Kollegen zu erwähnen… (Enter = senden)"
                : "Nachricht schreiben — @Name um jemanden zu erwähnen… (Enter = senden)"}
              rows={2}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              data-testid={`project-note-input-${internal ? "internal-" : ""}${projectId}`}
            />
            <label className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background hover:bg-secondary/60 cursor-pointer shrink-0" title="Foto oder PDF anhängen (max. 3, je 15 MB)">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              <input type="file" accept="image/*,application/pdf" multiple hidden onChange={onFilesChosen} data-testid={`project-note-attach-${internal ? "internal-" : ""}${projectId}`} />
            </label>
            <Button type="submit" size="sm" disabled={sending || (!body.trim() && pendingFiles.length === 0)} data-testid={`project-note-send-${internal ? "internal-" : ""}${projectId}`}>
              <Send className="h-3.5 w-3.5 mr-1" /> Senden
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
